import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  WorkspaceChrome,
  WorkspaceSummary,
  WorkspaceActions,
  WorkspaceSection,
  WorkspaceStat,
  tabFromQuery,
} from '../../../platform/workspace360';
import RecordNavigation from '../../../components/common/recordNavigation';
import CustomerPaymentModal from '../../../components/payment/CustomerPaymentModal';
import { downloadCustomerServiceAgreement } from '../../../services/customerServiceAgreementService';
import {
  getWallet,
  setAccountAutoRenew,
  getServiceChangeContext,
  requestServiceChange,
  cancelServiceChange,
} from '../../../services/portalRenewalService';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'related', label: 'Related' },
];

const ENABLE_LABELS = {
  true: 'Enabled',
  false: 'Disabled',
  1: 'Enabled',
  0: 'Disabled',
};

class Account360Panel extends Component {
  state = {
    activeTab: 'overview',
    autoRenewBusy: false,
    customerAutoRenewToggle: false,
    localAutoRenew: null,
    serviceChange: null,
    serviceChangeBusy: false,
    serviceChangeToSrvid: '',
    paying: false,
  };

  componentDidMount() {
    this.syncTab();
    this.loadWalletPolicy();
    this.loadServiceChange();
    this.setState({ localAutoRenew: !!this.props.account?.auto_renew_enabled });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.search !== this.props.location?.search) {
      this.syncTab();
    }
    if (
      prevProps.account?.auto_renew_enabled !== this.props.account?.auto_renew_enabled ||
      prevProps.account?.id !== this.props.account?.id
    ) {
      this.setState({ localAutoRenew: !!this.props.account?.auto_renew_enabled });
    }
    if (prevProps.account?.id !== this.props.account?.id) {
      this.loadServiceChange();
    }
  }

  loadWalletPolicy = async () => {
    try {
      const { data: payload } = await getWallet();
      const wallet = payload?.data || payload || {};
      this.setState({
        customerAutoRenewToggle: wallet.customerAutoRenewToggle !== false && wallet.autoRenewEnabled !== false,
      });
    } catch (_err) {
      this.setState({ customerAutoRenewToggle: false });
    }
  };

  loadServiceChange = async () => {
    const { account } = this.props;
    if (!account?.id || !this.isPrepaid()) {
      this.setState({ serviceChange: null });
      return;
    }
    try {
      const { data: payload } = await getServiceChangeContext(account.id);
      const ctx = payload?.data || payload || {};
      const first = (ctx.services || [])[0];
      this.setState({
        serviceChange: ctx,
        serviceChangeToSrvid: first ? String(first.id) : '',
      });
    } catch (_err) {
      this.setState({ serviceChange: null });
    }
  };

  handleRequestServiceChange = async () => {
    const { account } = this.props;
    const toSrvid = Number(this.state.serviceChangeToSrvid);
    if (!account?.id || !Number.isFinite(toSrvid) || toSrvid <= 0) return;
    this.setState({ serviceChangeBusy: true });
    try {
      await requestServiceChange(account.id, { to_srvid: toSrvid });
      toast.success('Service change scheduled for period end.');
      await this.loadServiceChange();
      if (typeof this.props.onReload === 'function') await this.props.onReload();
    } catch (ex) {
      toast.error(ex.response?.data?.message || 'Could not schedule Service Change.');
    } finally {
      this.setState({ serviceChangeBusy: false });
    }
  };

  handleCancelServiceChange = async () => {
    const { account } = this.props;
    const pending = this.state.serviceChange?.pending;
    if (!account?.id || !pending?.id) return;
    this.setState({ serviceChangeBusy: true });
    try {
      await cancelServiceChange(account.id, pending.id);
      toast.success('Service change cancelled.');
      await this.loadServiceChange();
      if (typeof this.props.onReload === 'function') await this.props.onReload();
    } catch (ex) {
      toast.error(ex.response?.data?.message || 'Could not cancel Service Change.');
    } finally {
      this.setState({ serviceChangeBusy: false });
    }
  };

  handleToggleAutoRenew = async () => {
    const { account } = this.props;
    if (!account?.id) return;
    const next = !this.state.localAutoRenew;
    this.setState({ autoRenewBusy: true });
    try {
      await setAccountAutoRenew(account.id, next);
      this.setState({ localAutoRenew: next });
      toast.success(next ? 'Auto Renew enabled.' : 'Auto Renew disabled.');
      if (typeof this.props.onReload === 'function') await this.props.onReload();
    } catch (ex) {
      toast.error(ex.response?.data?.message || 'Could not update Auto Renew.');
    } finally {
      this.setState({ autoRenewBusy: false });
    }
  };

  syncTab = () => {
    this.setState({
      activeTab: tabFromQuery(this.props.location?.search, 'overview', TABS),
    });
  };

  handleTabChange = (tabId) => {
    this.setState({ activeTab: tabId });
    const search = tabId === 'overview' ? '' : `?tab=${tabId}`;
    this.props.history.replace({ pathname: this.props.location.pathname, search });
  };

  serviceLabel() {
    const { account, serviceList } = this.props;
    if (account?.service_display_name) return account.service_display_name;
    const svc = account?.Service;
    if (svc?.parentService?.name) return svc.parentService.name;
    if (svc?.is_catalogue === false && typeof svc.name === 'string' && svc.name.includes(' — ')) {
      return svc.name.split(' — ').slice(1).join(' — ').trim() || svc.name;
    }
    if (svc?.name) return svc.name;
    const match = (serviceList || []).find(
      (s) => String(s.value) === String(account.srvid) || String(s.id) === String(account.srvid)
    );
    return match ? match.label : account.srvid;
  }

  isCustomerService() {
    const { account, serviceList } = this.props;
    const svc = account?.Service;
    if (svc) {
      return svc.is_catalogue === false || !!svc.contract_no || svc.contract_status != null;
    }
    const match = (serviceList || []).find(
      (s) => String(s.value) === String(account.srvid) || String(s.id) === String(account.srvid)
    );
    return (
      match?.is_catalogue === false || !!match?.contract_no || match?.contract_status != null
    );
  }

  /**
   * Match API resolveAccountBillingMode (Account → Service → default prepaid).
   * Auto Renew UI only when effective mode is prepaid — not inferred from contracts.
   */
  isPrepaid() {
    const { account, serviceList } = this.props;
    if (!account) return false;
    if (account.effective_billing_mode === 'prepaid' || account.effective_billing_mode === 'postpaid') {
      return account.effective_billing_mode === 'prepaid';
    }
    if (account.billing_mode === 'prepaid' || account.billing_mode === 'postpaid') {
      return account.billing_mode === 'prepaid';
    }
    const svc = account.Service;
    if (svc?.billing_mode === 'prepaid' || svc?.billing_mode === 'postpaid') {
      return svc.billing_mode === 'prepaid';
    }
    const match = (serviceList || []).find(
      (s) => String(s.value) === String(account.srvid) || String(s.id) === String(account.srvid)
    );
    if (match?.billing_mode === 'prepaid' || match?.billing_mode === 'postpaid') {
      return match.billing_mode === 'prepaid';
    }
    return true;
  }

  handleOpenAgreement = async () => {
    const { account } = this.props;
    const srvid = account?.srvid;
    if (!srvid) {
      toast.error('No service linked to this account.');
      return;
    }
    try {
      const contractNo = account?.Service?.contract_no;
      await downloadCustomerServiceAgreement(
        srvid,
        `agreement-${contractNo || srvid}.pdf`
      );
    } catch (ex) {
      const msg = ex.response?.data?.message || ex.response?.data;
      toast.error(typeof msg === 'string' && msg ? msg : 'Failed to open agreement');
    }
  };

  renderOverview() {
    const { account } = this.props;
    return (
      <WorkspaceSection title="Overview" icon="fa-user">
        <div className="customer360-grid-3">
          <WorkspaceStat label="Username" value={account.username} isStatic />
          <WorkspaceStat
            label="Status"
            value={ENABLE_LABELS[account.enableuser] || String(account.enableuser)}
            isStatic
          />
          <WorkspaceStat label="Service" value={this.serviceLabel()} isStatic />
          <WorkspaceStat label="Expiration" value={account.expiration} isStatic />
          {this.isPrepaid() ? (
            <WorkspaceStat
              label="Auto Renew"
              value={this.state.localAutoRenew ? 'On' : 'Off'}
              isStatic
            />
          ) : null}
          <WorkspaceStat label="Name" value={account.name} isStatic />
          <WorkspaceStat label="Company" value={account.company} isStatic />
          <WorkspaceStat label="Phone" value={account.phone} isStatic />
          <WorkspaceStat label="Mobile" value={account.mobile} isStatic />
          <WorkspaceStat label="Email" value={account.email} isStatic />
          <WorkspaceStat label="City" value={account.city} isStatic />
          <WorkspaceStat label="State" value={account.state} isStatic />
          <WorkspaceStat label="Country" value={account.country} isStatic />
        </div>
        {account.address ? (
          <div className="mt-3">
            <div className="customer360-snapshot__stat-label">Address</div>
            <div>{account.address}</div>
          </div>
        ) : null}
        {this.isPrepaid() &&
        this.state.serviceChange &&
        this.state.serviceChange.portalGate !== false &&
        (this.state.serviceChange.pending ||
          (this.state.serviceChange.services || []).length > 0) ? (
          <div className="mt-4">
            <h6 className="text-secondary">Service Change (at expiry)</h6>
            <p className="small text-muted mb-2">
              Applies when the current period ends. Auto Renew is not disabled.
            </p>
            {this.state.serviceChange.pending ? (
              <div className="d-flex align-items-center flex-wrap">
                <span className="mr-2">
                  Pending: {this.state.serviceChange.pending.from_srvid} →{' '}
                  {this.state.serviceChange.pending.to_srvid}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={this.state.serviceChangeBusy}
                  onClick={this.handleCancelServiceChange}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="form-inline">
                <select
                  className="form-control form-control-sm mr-2 mb-2"
                  value={this.state.serviceChangeToSrvid}
                  onChange={(e) => this.setState({ serviceChangeToSrvid: e.target.value })}
                >
                  {(this.state.serviceChange.services || []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mb-2"
                  disabled={this.state.serviceChangeBusy}
                  onClick={this.handleRequestServiceChange}
                >
                  Schedule
                </button>
              </div>
            )}
          </div>
        ) : null}
      </WorkspaceSection>
    );
  }

  renderRelated() {
    const { account } = this.props;
    return (
      <WorkspaceSection title="Related" icon="fa-link">
        <div className="customer360-grid-3">
          <WorkspaceStat
            label="Tickets"
            value={<Link to="/tickets">View tickets</Link>}
            isStatic
          />
          {this.isPrepaid() ? (
            <WorkspaceStat
              label="Payments"
              value={<Link to="/payments">View payments</Link>}
              isStatic
            />
          ) : (
            <WorkspaceStat
              label="Invoices"
              value={<Link to="/invoices">View invoices</Link>}
              isStatic
            />
          )}
          <WorkspaceStat label="Account" value={account.username} isStatic />
        </div>
      </WorkspaceSection>
    );
  }

  render() {
    const { account, history, location, navPaths } = this.props;
    if (!account || !account.username) return null;
    const { activeTab } = this.state;

    const summary = (
      <WorkspaceSummary
        title="Account Overview"
        icon="fa-server"
        stats={[
          {
            key: 'status',
            label: 'Status',
            value: ENABLE_LABELS[account.enableuser] || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'service',
            label: 'Service',
            value: this.serviceLabel() || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'expires',
            label: 'Expires',
            value: account.expiration || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
        ]}
      />
    );

    const actions = (
      <WorkspaceActions>
        {this.isCustomerService() && account.srvid != null ? (
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={this.handleOpenAgreement}
          >
            <i className="fa fa-file-pdf-o mr-1" aria-hidden /> Agreement
          </button>
        ) : null}
        {this.isPrepaid() && this.state.customerAutoRenewToggle ? (
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={this.handleToggleAutoRenew}
            disabled={this.state.autoRenewBusy}
            title={
              this.state.localAutoRenew
                ? 'Auto Renew is on — click to disable'
                : 'Auto Renew is off — click to enable'
            }
          >
            <i
              className={`fa fa-refresh mr-1 ${
                this.state.localAutoRenew ? 'text-success' : 'text-muted'
              }`}
              aria-hidden
            />
            {this.state.autoRenewBusy ? '…' : 'Auto Renew'}
          </button>
        ) : null}
        <Link className="btn btn-light btn-sm" to="/tickets/new">
          <i className="fa fa-life-ring mr-1" aria-hidden /> Ticket
        </Link>
        {this.isPrepaid() ? (
          <button type="button" className="btn btn-light btn-sm" onClick={() => this.setState({ paying: true })}>
            <i className="fa fa-credit-card mr-1" aria-hidden /> Pay Now
          </button>
        ) : (
          <Link className="btn btn-light btn-sm" to="/invoices">
            <i className="fa fa-file-text-o mr-1" aria-hidden /> Pay Invoice
          </Link>
        )}
      </WorkspaceActions>
    );

    return (
      <React.Fragment>
      <WorkspaceChrome
        title={account.username}
        subtitle={[account.name, account.company].filter(Boolean).join(' · ') || 'Account'}
        listLabel="Accounts"
        listTo="/accounts"
        recordLabel={account.username}
        nav={
          <RecordNavigation
            history={history}
            currentPath={location.pathname}
            paths={navPaths || []}
          />
        }
        summary={summary}
        actions={actions}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={this.handleTabChange}
      >
        {activeTab === 'related' ? this.renderRelated() : this.renderOverview()}
      </WorkspaceChrome>
      {this.state.paying && (
        <CustomerPaymentModal
          accountId={account.id}
          quantity={1}
          amount={Number(account.Service?.unitprice || account.unitprice || 0)}
          title="Pay subscription"
          onClose={() => this.setState({ paying: false })}
          onPaid={() => this.setState({ paying: false })}
        />
      )}
      </React.Fragment>
    );
  }
}

Account360Panel.propTypes = {
  account: PropTypes.object,
  serviceList: PropTypes.array,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navPaths: PropTypes.array,
};

export default Account360Panel;
